import { Trash2, Upload, ZoomIn, ZoomOut } from 'lucide-react';
import React, { useCallback, useState } from 'react';
import Cropper from 'react-easy-crop';
import { useChatbot } from '../../../contexts/ChatbotContext';
import { useDesignColors } from '../../../hooks/useDesignColors';
import { useLogos } from '../../../hooks/useLogos';
import { useLogoUpload } from '../../../hooks/useLogoUpload';

interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

export function LogoUploader() {
  const { chatbotPublicId } = useChatbot();
  const { uploadLogo, uploading, error, clearError } = useLogoUpload();
  const { colors } = useDesignColors();
  const { chatbotLogo, botIcon, setLogo } = useLogos();
  const [uploadingImage, setUploadingImage] = useState<'chatbot' | 'bot' | null>(null);
  const [uploadStatus, setUploadStatus] = useState<{
    chatbot?: { success: boolean; url?: string };
    bot?: { success: boolean; url?: string };
  }>({});

  // État pour le cropping
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<CropArea | null>(null);
  const [currentImage, setCurrentImage] = useState<string | null>(null);
  const [currentType, setCurrentType] = useState<'chatbot' | 'bot' | null>(null);

  const onCropComplete = useCallback((croppedArea: any, croppedAreaPixels: CropArea) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>, type: 'chatbot' | 'bot') => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Créer une URL pour l'aperçu
    const imageUrl = URL.createObjectURL(file);
    setCurrentImage(imageUrl);
    setCurrentType(type);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  }, []);

  const handleCropSave = async () => {
    if (!currentImage || !currentType || !croppedAreaPixels || !chatbotPublicId) return;

    try {
      setUploadingImage(currentType);
      clearError();

      // Créer un canvas pour le cropping
      const image = new Image();
      image.src = currentImage;
      await new Promise((resolve) => { image.onload = resolve; });

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Définir les dimensions du canvas
      canvas.width = croppedAreaPixels.width;
      canvas.height = croppedAreaPixels.height;

      // Dessiner l'image croppée
      ctx.drawImage(
        image,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        croppedAreaPixels.width,
        croppedAreaPixels.height
      );

      // Convertir en Blob en préservant la transparence
      const blob = await new Promise<Blob>((resolve) => {
        canvas.toBlob((blob) => {
          resolve(blob!);
        }, 'image/png');
      });

      // Créer un fichier à partir du blob
      const croppedFile = new File([blob], 'cropped-image.png', { type: 'image/png' });

      const url = await uploadLogo(croppedFile, currentType, chatbotPublicId);
      
      if (url) {
        setUploadStatus(prev => ({
          ...prev,
          [currentType]: { success: true, url }
        }));
      }

      // Nettoyer
      setCurrentImage(null);
      setCurrentType(null);
      URL.revokeObjectURL(currentImage);
    } catch (err) {
      console.error('Error processing image:', err);
      setUploadStatus(prev => ({
        ...prev,
        [currentType!]: { success: false }
      }));
    } finally {
      setUploadingImage(null);
    }
  };

  const handleCancel = () => {
    if (currentImage) {
      URL.revokeObjectURL(currentImage);
    }
    setCurrentImage(null);
    setCurrentType(null);
  };

  const handleRemoveLogo = async (type: 'chatbot' | 'bot') => {
    if (!chatbotPublicId) return;
    try {
      setUploadingImage(type);
      await setLogo(type, null, chatbotPublicId);
      setUploadStatus(prev => ({
        ...prev,
        [type]: undefined
      }));
    } catch (err) {
      console.error('Error removing logo:', err);
    } finally {
      setUploadingImage(null);
    }
  };

  const renderUploadZone = (type: 'chatbot' | 'bot', title: string) => {
    const currentLogo = type === 'chatbot' ? chatbotLogo : botIcon;

    return (
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          {title}
        </label>
        <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-lg transition-colors relative hover:border-indigo-500/50 group"
          style={{ borderColor: colors.gradientStart + '40', background: 'rgba(255, 255, 255, 0.03)' }}>
          {currentLogo ? (
            <div className="relative w-full aspect-square max-w-[160px]">
              <img 
                src={currentLogo} 
                alt={title}
                className="w-full h-full object-contain rounded-lg"
              />
              <div className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center gap-4">
                <label className="cursor-pointer text-white px-3 py-1.5 rounded-lg bg-indigo-500/20 hover:bg-indigo-500/30 transition-colors">
                  <span>Changer</span>
                  <input
                    type="file"
                    className="sr-only"
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, type)}
                  />
                </label>
                <button
                  onClick={() => handleRemoveLogo(type)}
                  className="text-white px-3 py-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 transition-colors flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-1 text-center">
              <Upload className="mx-auto h-12 w-12 text-gray-400" />
              <div className="flex text-sm text-gray-400">
                <label className="relative cursor-pointer rounded-md font-medium text-indigo-400 hover:text-indigo-300">
                  <span>Upload a file</span>
                  <input
                    type="file"
                    className="sr-only"
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, type)}
                  />
                </label>
              </div>
              <p className="text-xs text-gray-400">PNG, JPG up to 2MB</p>
            </div>
          )}
          {uploadingImage === type && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-lg">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500" />
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-500/10 text-red-400 p-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {currentImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
          <div className="bg-gray-900 rounded-xl p-6 w-full max-w-2xl">
            <h3 className="text-lg font-semibold text-white mb-4">Adjust the image</h3>
            
            <div className="relative h-96 mb-4">
              <Cropper
                image={currentImage}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>

            <div className="flex items-center gap-4 mb-6">
              <button
                onClick={() => setZoom(Math.max(1, zoom - 0.1))}
                className="p-2 text-gray-400 hover:text-white transition-colors"
              >
                <ZoomOut className="w-5 h-5" />
              </button>
              
              <input
                type="range"
                value={zoom}
                min={1}
                max={3}
                step={0.1}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="flex-1"
              />
              
              <button
                onClick={() => setZoom(Math.min(3, zoom + 0.1))}
                className="p-2 text-gray-400 hover:text-white transition-colors"
              >
                <ZoomIn className="w-5 h-5" />
              </button>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={handleCancel}
                className="px-4 py-2 rounded-lg bg-white/5 text-gray-300 hover:bg-white/10 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCropSave}
                className="px-4 py-2 rounded-lg bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30 transition-colors"
              >
                Validate
              </button>
            </div>
          </div>
        </div>
      )}

      {renderUploadZone('chatbot', 'Chatbot logo')}
      {renderUploadZone('bot', 'Reply icon')}
    </div>
  );
}